import { useSequenceStore } from '~/training/sequence/SequenceStore';
import { getParentWindow, shouldUseParentHost } from './host';
import { InteractionArgs, LmsAdapter } from './types';

/**
 * Shape of the global object historically published by our RXD wrapper on
 * `window.parent.RXD`. Methods are loosely typed because the surface has
 * accreted slightly different signatures over time and unifying them is
 * intentionally out of scope for this refactor.
 */
interface RxdGlobal {
  SetProgressMeasure?: (value: number) => void;
  SetScore?: (score: number, maxScore: number, minScore: number) => void;
  SetReachedEnd?: () => void;
  SetPassed?: () => void;
  ConcedeControl?: () => void;
  RecordMultipleChoiceInteraction?: (...args: unknown[]) => void;
  RecordTrueFalseInteraction?: (...args: unknown[]) => void;
  RecordSequencingInteraction?: (...args: unknown[]) => void;
  RecordFillInInteraction?: (...args: unknown[]) => void;
}

/**
 * Re-reads `window.parent.RXD` on every call. This is intentional: the
 * Rustici Cross-Domain wrapper installs `window.parent.RXD` *asynchronously*
 * (its driver is loaded from a separate script tag and runs through a
 * `setTimeout`-driven `IndexApiStart` poll). If we cached the lookup at
 * adapter-creation time we would lock ourselves into "no RXD" whenever our
 * SPA bootstraps faster than the driver — which is exactly what was happening
 * in production.
 *
 * The original `preprod` code did the same thing inline in every component
 * that touched RXD; we centralise the read here but keep the per-call
 * semantics identical.
 */
function readRxd(): RxdGlobal | undefined {
  if (!shouldUseParentHost()) {
    return undefined;
  }

  const parent = getParentWindow();
  if (!parent) {
    return undefined;
  }

  try {
    return (parent as Window & { RXD?: RxdGlobal }).RXD;
  } catch {
    return undefined;
  }
}

function isInteractionsEnabled(): boolean {
  try {
    return useSequenceStore.getState().sendRxdInteractions !== false;
  } catch {
    return true;
  }
}

function safeCall<T extends (...args: never[]) => unknown>(
  fn: T | undefined,
  args: unknown[]
): void {
  if (typeof fn !== 'function') {
    return;
  }
  try {
    (fn as (...a: unknown[]) => unknown)(...args);
  } catch (error) {
    console.error('[lms/rxd] call failed:', error);
  }
}

/**
 * Always returns an adapter. Whether it is actually wired up to a real RXD
 * host is decided per-call via {@link readRxd} — see the rationale on that
 * function for why eager detection at create time is wrong.
 */
export function createRxdAdapter(): LmsAdapter {
  const callDirect = (
    pick: (rxd: RxdGlobal) => ((...args: unknown[]) => void) | undefined,
    args: unknown[]
  ) => {
    const rxd = readRxd();
    if (!rxd) return;
    safeCall(pick(rxd), args);
  };

  const callInteraction = (
    pick: (rxd: RxdGlobal) => ((...args: unknown[]) => void) | undefined,
    args: InteractionArgs
  ) => {
    if (!isInteractionsEnabled()) return;
    callDirect(pick, [...args]);
  };

  return {
    name: 'rxd',
    get isAvailable(): boolean {
      return readRxd() !== undefined;
    },

    setProgressMeasure: value =>
      callDirect(rxd => rxd.SetProgressMeasure, [value]),
    setScore: (score, maxScore, minScore) =>
      callDirect(rxd => rxd.SetScore, [score, maxScore, minScore]),
    setReachedEnd: () => callDirect(rxd => rxd.SetReachedEnd, []),
    setPassed: () => callDirect(rxd => rxd.SetPassed, []),
    concedeControl: () => callDirect(rxd => rxd.ConcedeControl, []),

    recordMultipleChoiceInteraction: (...args) =>
      callInteraction(rxd => rxd.RecordMultipleChoiceInteraction, args),
    recordTrueFalseInteraction: (...args) =>
      callInteraction(rxd => rxd.RecordTrueFalseInteraction, args),
    recordSequencingInteraction: (...args) =>
      callInteraction(rxd => rxd.RecordSequencingInteraction, args),
    recordFillInInteraction: (...args) =>
      callInteraction(rxd => rxd.RecordFillInInteraction, args),

    // RXD's public API on `window.parent.RXD` is a curated subset (Score /
    // ReachedEnd / Passed / Record*Interaction / ConcedeControl) — it does
    // not expose generic CMI reads such as `cmi.student_preference.language`.
    // The data model field is technically populated on the underlying SCORM
    // session that RXD wraps, but we'd need RXD itself to surface it. Until
    // then this adapter treats "no preference" as the right answer; the
    // composite adapter will then defer to scorm-again if that's also wired
    // up, and otherwise the consumer falls back to `navigator.language`.
    getLearnerLanguagePreference: () => Promise.resolve('')
  };
}
